import React, { useState, useEffect } from 'react';
import { School, Plus, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { getCampuses, addCampus, removeCampus, formatApiError } from '../services/api';

const TYPE_LABELS = {
  elementary: 'Elementary', middle: 'Middle', high: 'High School', k8: 'K-8', k12: 'K-12',
  alternative: 'Alternative', district_office: 'District Office', other: 'Other',
};

export function CampusManager({ canEdit = true }) {
  const [campuses, setCampuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [type, setType] = useState('elementary');
  const [enrollment, setEnrollment] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    getCampuses()
      .then((res) => { setCampuses(res.data.campuses || []); setTypes(res.data.types || []); })
      .catch(() => setError('Could not load campuses'))
      .finally(() => setLoading(false));
  }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const res = await addCampus({ name, type, enrollment: enrollment ? parseInt(enrollment, 10) : null });
      setCampuses((prev) => [...prev, res.data.campus]);
      setName('');
      setEnrollment('');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Remove this campus? Existing incidents and actions keep their campus label.')) return;
    try {
      await removeCampus(id);
      setCampuses((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const totalEnrollment = campuses.reduce((s, c) => s + (c.enrollment || 0), 0);

  return (
    <div data-testid="campus-manager">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-outfit text-lg font-semibold text-slate-900">Campuses</h3>
          <p className="text-sm text-slate-500">Tag incidents and improvement actions by school building.</p>
        </div>
        <span className="text-sm text-slate-500">{campuses.length} campus{campuses.length === 1 ? '' : 'es'}{totalEnrollment ? ` · ${totalEnrollment.toLocaleString()} students` : ''}</span>
      </div>

      {error && <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2" data-testid="campus-error"><AlertCircle className="w-4 h-4" />{error}</div>}

      {canEdit && (
        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-[1fr_160px_120px_auto] gap-3 mb-6" data-testid="add-campus-form">
          <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Campus name (e.g. Lincoln Elementary)"
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="campus-name-input" />
          <select value={type} onChange={(e) => setType(e.target.value)} className="px-3 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="campus-type-select">
            {(types.length ? types : Object.keys(TYPE_LABELS)).map((t) => <option key={t} value={t}>{TYPE_LABELS[t] || t}</option>)}
          </select>
          <input type="number" min="0" value={enrollment} onChange={(e) => setEnrollment(e.target.value)} placeholder="Students"
            className="px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="campus-enrollment-input" />
          <button type="submit" disabled={saving || !name.trim()} className="px-4 py-2.5 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="add-campus-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
          </button>
        </form>
      )}

      {loading ? (
        <p className="text-slate-400 text-sm animate-pulse">Loading campuses...</p>
      ) : campuses.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-xl p-8 text-center text-slate-500" data-testid="campus-empty">
          <School className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          No campuses yet. Add each school building so incidents and actions can be tracked by site.
        </div>
      ) : (
        <div className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden" data-testid="campus-list">
          {campuses.map((c) => (
            <div key={c.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50" data-testid={`campus-${c.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><School className="w-4 h-4 text-blue-600" /></div>
                <div>
                  <p className="font-medium text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{TYPE_LABELS[c.type] || c.type}{c.enrollment ? ` · ${c.enrollment.toLocaleString()} students` : ''}</p>
                </div>
              </div>
              {canEdit && (
                <button onClick={() => handleRemove(c.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`remove-campus-${c.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
